import React from 'react';
import { Box, Tooltip, Typography, useTheme } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
    faCalendar,
    faCalendarCircleExclamation,
    faCalendarClock,
    faBell,
    faBellRing,
} from '@fortawesome/sharp-regular-svg-icons';
import { formatRelativeDate } from '@europrocurement/l2d-utils';

type CheckableTaskRowDatesSectionProps = {
    expiredAt?: string | null;
    remindAt?: string | null;
    isMini: boolean;
};

const CheckableTaskRowDatesSection: React.FunctionComponent<CheckableTaskRowDatesSectionProps> =
    function ({ expiredAt, remindAt, isMini }) {
        const { palette } = useTheme();

        if (!expiredAt && !remindAt) return null;

        const now = new Date();
        now.setHours(0, 0, 0, 0);

        let taskExpiredColor = palette.text.primary;
        let calendarIcon = faCalendar;
        if (expiredAt) {
            const taskExpired = new Date(expiredAt);
            taskExpired.setHours(0, 0, 0, 0);
            if (taskExpired < now) {
                taskExpiredColor = palette.error.main;
                calendarIcon = faCalendarCircleExclamation;
            } else if (taskExpired.getTime() === now.getTime()) {
                taskExpiredColor = palette.warning.main;
                calendarIcon = faCalendarClock;
            }
        }

        let remindColor: string | undefined;
        let bellIcon = faBell;
        if (remindAt) {
            const taskRemind = new Date(remindAt);
            taskRemind.setHours(0, 0, 0, 0);
            if (taskRemind < now) {
                remindColor = palette.error.main;
                bellIcon = faBellRing;
            } else if (taskRemind.getTime() === now.getTime()) {
                remindColor = palette.warning.main;
                bellIcon = faBellRing;
            }
        }

        return (
            <Box sx={{ display: 'flex', gap: '6px', alignItems: 'center' }}>
                {expiredAt &&
                    (isMini ? (
                        <Tooltip title={formatRelativeDate(expiredAt)}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: taskExpiredColor,
                                    cursor: 'default',
                                }}
                            >
                                <FontAwesomeIcon icon={calendarIcon} />
                            </Box>
                        </Tooltip>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                color: taskExpiredColor,
                            }}
                        >
                            <FontAwesomeIcon icon={calendarIcon} />
                            <Typography
                                variant="l2dcaption"
                                sx={{ fontWeight: 400 }}
                            >
                                {formatRelativeDate(expiredAt)}
                            </Typography>
                        </Box>
                    ))}
                {remindAt &&
                    (isMini ? (
                        <Tooltip title={formatRelativeDate(remindAt)}>
                            <Box
                                sx={{
                                    display: 'flex',
                                    alignItems: 'center',
                                    color: remindColor,
                                    cursor: 'default',
                                }}
                            >
                                <FontAwesomeIcon icon={bellIcon} />
                            </Box>
                        </Tooltip>
                    ) : (
                        <Box
                            sx={{
                                display: 'flex',
                                alignItems: 'center',
                                gap: '3px',
                                color: remindColor,
                            }}
                        >
                            <FontAwesomeIcon icon={bellIcon} />
                            <Typography
                                variant="l2dcaption"
                                sx={{ fontWeight: 400 }}
                            >
                                {formatRelativeDate(remindAt)}
                            </Typography>
                        </Box>
                    ))}
            </Box>
        );
    };

export default CheckableTaskRowDatesSection;
